import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";

// URLs for files, without the session token in them.
//
// `fileUrl(id)` used to be `${API}/files/${id}?auth=${sessionJWT}` — the whole
// account, in the query string of every rendered image, and so in the browser
// history, in every proxy log, and in every devtools screenshot in every bug
// report. What replaces it is a token the server mints for one file.
//
// Two things make that affordable:
//
//   Batching. A message list wants thirty files at once; asking thirty times
//   would be worse than the problem. Requests made in the same tick go in one
//   POST.
//
//   Stable URLs. The server's token is identical for the whole 15-minute window
//   it belongs to, so the URL an <img> gets does not change between renders and
//   the browser cache keeps working. A fresh token per render would key every
//   image differently and re-download all of them, every time.

const cache = new Map();      // file_id -> { token, expires_at }
const subs = new Map();       // file_id -> Set<fn>
let queue = new Set();
let scheduled = false;

const now = () => Date.now() / 1000;
// Refreshed a little early, so an <img> is never handed a token that dies while
// the request is in flight.
// Two different questions. "Do we still have a live answer for this id" decides
// whether to ask again — and a refusal is a live answer, or the same denied file
// is asked for on every render. "Is there a URL" additionally needs a token; a
// denied entry has none, and without this check it rendered `?t=null`.
const known = (entry) => entry && entry.expires_at - 30 > now();
const fresh = (entry) => known(entry) && Boolean(entry.token);

function emit(id) {
  const set = subs.get(id);
  if (set) set.forEach((fn) => fn(urlFor(id)));
}

/** The URL for a file, or null while its token is being fetched. */
export function urlFor(id) {
  if (!id) return null;
  const entry = cache.get(id);
  return fresh(entry) ? `${API}/files/${id}?t=${encodeURIComponent(entry.token)}` : null;
}

async function flush() {
  scheduled = false;
  const ids = [...queue];
  queue = new Set();
  if (!ids.length) return;
  try {
    const { data } = await api.post("/files/access", { file_ids: ids });
    const expires_at = data.expires_at;
    for (const [id, token] of Object.entries(data.tokens || {})) {
      cache.set(id, { token, expires_at });
      emit(id);
    }
    // A file that is gone or not ours is remembered as such, so the same
    // request is not retried on every render for the rest of the session.
    for (const id of data.denied || []) {
      cache.set(id, { token: null, expires_at: now() + 300 });
      emit(id);
    }
  } catch {
    for (const id of ids) emit(id);   // callers stay in "loading"; a later render retries
  }
}

export function request(id) {
  if (!id || known(cache.get(id))) return;
  queue.add(id);
  if (!scheduled) {
    scheduled = true;
    // A microtask, not a timer: everything rendering in this pass joins the
    // same batch, and nothing waits on a clock.
    Promise.resolve().then(flush);
  }
}

export function subscribe(id, fn) {
  if (!subs.has(id)) subs.set(id, new Set());
  subs.get(id).add(fn);
  return () => subs.get(id)?.delete(fn);
}

/** The URL for a file, once the server has said this person may read it. */
export function useFileUrl(id) {
  const [url, setUrl] = useState(() => urlFor(id));
  useEffect(() => {
    if (!id) { setUrl(null); return undefined; }
    setUrl(urlFor(id));
    const off = subscribe(id, setUrl);
    request(id);
    return off;
  }, [id]);
  return url;
}

export function __resetFileAccess() {
  cache.clear();
  subs.clear();
  queue = new Set();
  scheduled = false;
}
